/* eslint-disable lit/binding-positions */
import { CombineOperator, CustomFieldCustomization, GetPointResponse, PointCardView, PointOrderingOption, TagSearchType } from '../types';
import { GoogleTagEvent, logWithAmplitude, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, TemplateResult, css, html } from 'lit';
import { categoryFilterFn, checkPointWithinDistance, keywordSearchFn, pointSearch, tagsFilterFn } from '../helpers/filters';
import { customElement, query, state } from 'lit/decorators';
import { debounce, isDefined, kilometersOrMiles } from '../helpers/utils';
import { detectMobileViewport, inIframe } from '../helpers/device';

import { AdvancedFilteringModal } from '../components/advanced-filtering-modal';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { getDateTimeStringForPoint } from '../helpers/date';
import { getMainPage } from '../helpers/page-name';
import { getQueryStringParameters } from '../helpers/urls';
import { loadGoogleMapsApi } from '../helpers/api/google-api';
import { parseDescriptionText } from '../helpers/embeds';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

@customElement('summary-page')
export class SummaryPage extends LitElement {
    amplitude_callsite: string = 'Summary Page';

    @query('#search')
    search!: HTMLInputElement;

    @query('advanced-filtering-modal')
    advancedFilteringModal!: AdvancedFilteringModal;

    @state()
    options: string[] = ['Category', 'Alphabetical', 'Distance'];

    @state()
    selected_tab: string = 'Category';

    @state()
    sorted_points: Array<[string, GetPointResponse[], number]> = [];

    @state()
    event_strings: { [key: string]: string } = {};

    @state()
    showSummaryList: boolean = false;

    @state()
    showAdvancedSearch: boolean = false;

    @state()
    showSearch: boolean = false;

    @state()
    searchText: string = '';

    @state()
    search_distance: number = 0;

    @state()
    geometry_ready =  false;

    @state()
    listView: PointCardView = PointCardView.Table;

    @state()
    private renderedRows: TemplateResult[] = [];

    @state()
    private readonly showMoreDescMap: Map<string, boolean> = new Map();

    @state()
	tagFilterArray: TagSearchType = {
        operator: CombineOperator.And,
        tags: [],
    };

    @state()
    currentSpreadsheetPage: number = 1;

    @state()
    itemsPerPage: number = 25;

    @state()
    loadingMore: boolean = false;

    @state()
    hideHeader: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }


    connectedCallback() {
        super.connectedCallback();
        this.addHoverListener();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeHoverListener();
    }

    addHoverListener() {
        const lastItem = this.shadowRoot?.querySelector('.grid-row:last-child');
        if (lastItem) {
            lastItem.addEventListener('mouseenter', this.handleHover);
        }
    }

    removeHoverListener() {
        const lastItem = this.shadowRoot?.querySelector('.grid-row:last-child');
        if (lastItem) {
            lastItem.removeEventListener('mouseenter', this.handleHover);
        }
    }

    handleHover = () => {
        if (!this.loadingMore) {
            this.changePage(this.currentSpreadsheetPage + 1);
            this.requestUpdate();
        }
    };

    async getEventString(point: GetPointResponse): Promise<string | undefined> {
        if (topicData.topic) {
            return await getDateTimeStringForPoint(point, topicData.topic);
        } else {
            return '';
        }
    }

    private async startup() {
        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            false,
            true
        );
        if (isDefined(topicData.topic)) {
            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                false,
                false,
                challengeData.challenge ? challengeData.allCheckins : accountInfo.account_checkins
            );
        }

        const queryString = getQueryStringParameters();

        const hideHeader =
            queryString.get('hide_summary_header') === 'true';
        this.hideHeader = hideHeader;

        // if topic has date time, add by date/time as an option to the front and set it as the default
        if (topicData.topic?.has_dates === true) {
            await this.generateEventStrings(pointCache.getSortedPoints());
            this.options = ['Chronological', ...this.options];
            this.selected_tab = 'Chronological';
            this.sortPointsByDate();
        } else if (topicData.topic?.enable_point_numbering === true) {
            this.options = ['Number', ...this.options];
            this.sortPointsByNumber();
        } else {
            this.sortPointsByCategory();
        }

        if (topicData.topic?.has_likes === true) {
            this.options = [...this.options, 'Likes'];
        }

        this.listView = topicData.topic?.default_list_view === PointCardView.List ? PointCardView.Table : topicData.topic?.default_list_view ?? PointCardView.Table;

        // set custom color and custom text color variables
        const root = this.shadowRoot?.host as HTMLElement;
        if (isDefined(root) && isDefined(root?.style)) {
            root.style.setProperty('--custom-color', topicData.backgroundColor);
            root.style.setProperty('--custom-text-color', topicData.textColor);
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));
        this.requestUpdate();

        await this.tryLoadGoogleMapsApi();

        // Track summary page view
        logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.SUMMARY_PAGE_VIEW);
    }

    async tryLoadGoogleMapsApi() {
        try {
            await loadGoogleMapsApi('geometry');
            this.geometry_ready = true;
        } catch (e) {
            console.error('Failed to load Google Maps API', e);
            this.geometry_ready = false;
        }
    }

    async generateEventStrings(points: GetPointResponse[]) {
        for (const point of points) {
            this.event_strings[point._id.$oid] = await this.getEventString(point) ?? '';
        }
    }

    async searchByDistance(e: CustomEvent) {
        const radius_string = (e.target as HTMLInputElement).value;

        if (radius_string === '') {
            return;
        }

        let radius = parseFloat(radius_string);
        this.search_distance = parseFloat(radius_string);

        let user_location : { lat: number, lng: number } | undefined;

        if (this.search_distance > 0) {
            user_location = await this.getUserLocation();
        }

        if (kilometersOrMiles() === 'mi') {
            // convert to meters
            radius = radius * 1609.34;
        } else {
            // convert to meters
            radius = radius * 1000;
        }

        if (radius === 0) {
            pointCache.removeFilter('DistanceFilter');
            return;
        }

        if (isDefined(user_location)) {
            pointCache.filterMarkers({
                name: 'DistanceFilter',
                filterFn: (p: GetPointResponse) => {
                    return checkPointWithinDistance(p, user_location, radius);
                },
            });
        } else {
            getMainPage().errorToastElement.showToast('Could not get your location');
        }
        this.recalculatePoints();
    }

    sortPointsBySearch() {
        const points = pointCache.getSortedPoints();
        this.sorted_points = [[`Results for "${this.searchText}"`, points, 1]];
    }

    sortPointsByCategory() {
        this.sorted_points = [];
        pointCache.pointOrdering = [PointOrderingOption.Category, PointOrderingOption.Alphabetical];
        const points = pointCache.getSortedPoints();
        for (const category of topicData.categories) {
            const category_id = category._id?.$oid;
            const category_points = points.filter((point) => point.topic_category_id?.$oid === category_id);
            this.sorted_points.push([category.name, category_points, 1]);
        }
    }

    sortPointsByName() {
        pointCache.pointOrdering = [PointOrderingOption.Alphabetical];
        const points = pointCache.getSortedPoints();
        // group points by the first letter of the name
        const sorted_points = points.reduce<{ [key: string]: GetPointResponse[] }>((acc, point) => {
            const first_letter = (point.name ?? point.location.search).charAt(0).toUpperCase();
            if (acc[first_letter] === undefined) {
                acc[first_letter] = [];
            }
            acc[first_letter].push(point);
            return acc;
        }, {});
        // sort the keys
        const sorted_keys = Object.keys(sorted_points).sort();
        this.sorted_points = sorted_keys.map((key) => [key, sorted_points[key], 1]);
    }

    sortPointsByDate() {
        pointCache.pointOrdering = [PointOrderingOption.Start];
        const points = pointCache.getSortedPoints();
        // group points by the date
        const sorted_points = points.reduce<{ [key: string]: GetPointResponse[] }>((acc, point) => {
            const date = point.start?.split('T')[0];
            if (date !== undefined) {
                if (acc[date] === undefined) {
                    acc[date] = [];
                }
                acc[date].push(point);
            } else {
                if (acc['No Date'] === undefined) {
                    acc['No Date'] = [];
                }
                acc['No Date'].push(point);
            }
            return acc;
        }, {});
        // sort the keys
        const sorted_keys = Object.keys(sorted_points).sort();
        this.sorted_points = sorted_keys.map((key) => [key, sorted_points[key], 1]);
    }

    getUserLocation = async (): Promise<{ lat: number, lng: number }> => {
        return await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => reject(error)
            );
        });
    };

    async sortPointsByDistance() {
        pointCache.pointOrdering = [PointOrderingOption.Distance];
        // get user location

        let user_location: { lat: number, lng: number } | undefined;
        try {
            user_location = await this.getUserLocation();
        } catch (e) {
            console.error('Failed to get user location', e);
            user_location = undefined;
            getMainPage().errorToastElement?.showToast('Failed to get user location');
            this.sortPointsByName();
            return;
        }

        const points = pointCache.getSortedPoints(user_location);
        this.sorted_points = [['', points, 1]];
    }

    sortPointsByNumber() {
        pointCache.pointOrdering = [PointOrderingOption.Number];
        const points = pointCache.getSortedPoints();
        this.sorted_points = [['', points, 1]];
    }

    // sortPointsByLikes() {
    //     pointCache.pointOrdering = [PointOrderingOption.
    //     const points = pointCache.getSortedPoints();
    //     this.sorted_points = [['', points]];
    // }

    private goToNextPageByGroup(group_name: string) {
        this.sorted_points = this.sorted_points.map(([name, points, page]) => {
            if (name === group_name) {
                return [name, points, page + 1];
            }
            return [name, points, page];
        }
    );
    }

    async handleChangeTab(e: CustomEvent) {
        this.selected_tab = (e.target as HTMLInputElement).value;

        if (this.selected_tab === 'Category') {
            this.sortPointsByCategory();
        }

        if (this.selected_tab === 'Alphabetical') {
            this.sortPointsByName();
        }

        if (this.selected_tab === 'Distance') {
            await this.sortPointsByDistance();
        }

        // if (this.selected_tab === 'Likes') {
        //     this.sortPointsByLikes();
        // }

        if (this.selected_tab === 'Chronological') {
            this.sortPointsByDate();
        }

        if (this.selected_tab === 'Number') {
            this.sortPointsByNumber();
        }
    }

    private scrollToPoint(point_id: string) {
        const point = this.shadowRoot?.getElementById(`point-${point_id}`);
        if (isDefined(point)) {
            point.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // close the summary list
        this.showSummaryList = false;
    }

    private readonly searchChanged = debounce(this.searchChangedInternal, 500);
    private searchChangedInternal() {
        const search = this.search.value?.trim().toLocaleLowerCase() ?? '';

        logWithAmplitude(this.amplitude_callsite, 'Search', { search });

        pointCache.removeFilter('ListSearch');
        if (search !== '') {
            pointCache.filterMarkers({ name: 'ListSearch', filterFn: (p: GetPointResponse) => pointSearch(p, search) });
            this.searchText = search;
            logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.MAP_SEARCH, {
                search_query: search,
            });
        }
        this.selected_tab = 'Text Search';
        this.recalculatePoints();
    }

    private async openThisMap() {
        // get topic key
        const queryString = window.location.href.split('?')[1];
        const params = new URLSearchParams(queryString);
        const topicKey = params.get('topic_key');

        // open URL
        const openURL = `${window.location.origin}/#command-center?topic_id=${topicData.topicId}&topic_key=${topicKey ?? ''}`;
        window.open(openURL, '_blank');
    }

    private toggleSummaryList() {
        this.showSummaryList = !this.showSummaryList;
        if (this.showSummaryList) {
            this.showAdvancedSearch = false;
            this.showSearch = false;
        }
    }

    toggleShowSearch() {
        this.showSearch = !this.showSearch;
        if (this.showSearch) {
            this.showAdvancedSearch = false;
            this.showSummaryList = false;
        }
    }

    clearFilters(fromModal: boolean = false) {
        pointCache.removeFilter();
        if (this.selected_tab === 'Text Search') {
            this.search.value = '';
            this.selected_tab = 'Category';
        }
        this.searchText = '';
        this.showSearch = false;
        this.showAdvancedSearch = false;
        pointCache.categoryFilterList = [];
        this.recalculatePoints();

        if (!fromModal) {
            this.advancedFilteringModal.resetFilters();
        }
    }

    private readonly advancedSearchFilterEvent = async (e: any) => {
        pointCache.removeFilter();

        pointCache.categoryFilterList = e.detail.selectedCategories;
        pointCache.tagFilterList =  e.detail.selectedTopTags.map((t: { topLevelTag: any; }) => t.topLevelTag);
        pointCache.tagFilterList = pointCache.tagFilterList.concat(
            (e.detail.selectedSubTags ?? []).map((t: { subTags: any; }) => t.subTags).flat()
        );

        const categoryFilter = e.detail?.selectedCategories.length > 0 ? categoryFilterFn : (p: GetPointResponse) => true;
        const tagsFilter = e.detail.selectedTags.tags.length > 0 ? tagsFilterFn : (p: GetPointResponse) => true;
        const keywordsFilter = e.detail?.keywordsList.length > 0 ? keywordSearchFn : (p: GetPointResponse) => true;

        pointCache.filterMarkers({
            name: 'FilterChip',
            filterFn: (p: GetPointResponse) =>
                categoryFilter(p, e.detail.selectedCategories) &&
                tagsFilter(p, e.detail.selectedTags) &&
                keywordsFilter(p, e.detail.keywordsList),
        });

        this.showAdvancedSearch = false;
        this.showSearch = false;
        this.recalculatePoints();
    };

    updateCategoryFilter(type: string, values: string[]) {
            const category_names = values;
            pointCache.filterMarkers({
                name: 'CategoryFilter',
                filterFn: (p: GetPointResponse) =>
                    categoryFilterFn(p, category_names),
            });
            pointCache.categoryFilterList = category_names;

            for (const category of topicData.categories) {
                logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.MAP_CATEGORY_CLICKED, {
                    category_name: category.name,
                });
            }

        this.recalculatePoints();
    }

    // updateTagFilter(type: string, value: string[] | string, topLevel: boolean) {
    //     // if it is top level see if it is already in this.tagFilterArray, if it is remove it, if it is not add it
    //     const selectedTopTags = this.tagFilterArray.tags.map((tag) => tag.topLevelTag);
    //     const selectedSubTags = this.tagFilterArray.tags.map((tag) => tag.subTags).flat();
    //     if (topLevel) {
    //         value = value as string;
    //         if (this.tagFilterArray.tags.map((tag) => tag.name).includes(value)) {
    //             this.tagFilterArray.tags = this.tagFilterArray.tags.filter((tag) => tag !== value);
    //         } else {
    //             this.tagFilterArray.tags.push(value);
    //         }
    //     } else {
    //         // if it is a sub tag, find the top level tag and add the sub tag to the top level tag
    //         const topLevelTag = this.tagFilterArray.tags.find((tag) => tag.name === value);
    //         if (topLevelTag) {
    //             topLevelTag.subTags = [...topLevelTag.subTags, value];
    //         } else {
    //             this.tagFilterArray.tags.push({ name: value, subTags: [value] });
    //         }
    //     }

    //     pointCache.filterMarkers({
    //         name: 'TagFilter',
    //         filterFn: (p: GetPointResponse) =>
    //             tagsFilterFn(p, { tags: tag_names, operator: CombineOperator.Or }),
    //     });
    //     pointCache.tagFilterList = tag_names;
    //     this.recalculatePoints();
    // }


    recalculatePoints() {
        if (this.selected_tab === 'Chronological') {
            this.sortPointsByDate();
        } else if (this.selected_tab === 'Category') {
            this.sortPointsByCategory();
        } else if (this.selected_tab === 'Alphabetical') {
            this.sortPointsByName();
        } else if (this.selected_tab === 'Distance') {
            void this.sortPointsByDistance();
        // } else if (this.selected_tab === 'Likes') {
        //     this.sortPointsByLikes();
        } else if (this.selected_tab === 'Number') {
            this.sortPointsByNumber();
        } else if (this.selected_tab === 'Text Search') {
            this.sortPointsBySearch();
        } else {
            this.sortPointsByCategory();
        }
    }

    formattedDate(date: string) {
        return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    render() {
        return topicData.isReady
            ? inIframe() ? this.renderInnerContent() :
             html`
                <div class="fixed_top" style="background-color: ${topicData.backgroundColor};">
                </div>
                <div class="summary__page_wrapper" style="background-color: ${topicData.backgroundColor}">
                    <div class="points__content_wrapper">
                        ${this.hideHeader ? '' : html`
                            <div class="points__header">
                                <h1>${topicData.topic?.name}</h1>
                                <h2>${topicData.topic?.creator ? html`<p>${topicData.topic?.creator}</p>` : ''}</h2>
                                <div class="share__buttons">
                                    <form-button
                                        variant="outline"
                                        size="md"
                                        @click=${this.openThisMap}
                                    >Open Map</form-button>
                                </div>
                            </div>
                            ${topicData.topic?.description ? html`
                                <div class="points__middle_content description_section">
                                    ${topicData.topic?.description}
                                </div>
                            ` : ''}
                            <hr>
                        `}
                        ${this.renderInnerContent()}

                    </div>
                </div>
                <error-toast id="errorToast"></error-toast> `
            : '';
    }

    renderInnerContent() {
        return html`
         <div class="filter_area_sticky">
                        <div class="filter_area">
                            <div class="filter_options">
                                ${this.showSearch ? html`
                                    <proxi-textfield
                                        style="width: 200px; padding: 0; height: 28px;"
                                        id="search"
                                        class="input"
                                        .height=${'28px'}
                                        outlined
                                        placeholder="Search"
                                        @input="${this.searchChanged}">
                                    </proxi-textfield>
                                ` : html`
                                    <form-button
                                        .icon=${'search'}
                                        variant="outline"
                                        size="sm"
                                        @click=${this.toggleShowSearch}
                                    >Search</form-button>
                                    `}
                                ${this.selected_tab === 'Text Search' ? '' : html`
                                    ${topicData.categories.length > 1 ? html`
                                        <proxi-multi-selectfield
                                            outlined
                                            .height=${'28px'}
                                            title="Select Categories"
                                            class="input reward-criteria"
                                            .options=${topicData.categories.map((category) => ({text: category.name, value: category.name}))}
                                            .selectedValues=${pointCache.categoryFilterList.map((category) => ({text: category, value: category}))}
                                            @selection-changed=${(e: CustomEvent) => this.updateCategoryFilter('category', e.detail.selectedValues.map((item: any) => item.value))}
                                        >
                                        </proxi-multi-selectfield>
                                    ` : ''}
                                `}
                                ${this.geometry_ready ? html`
                                    <proxi-selectfield
                                        id="submission_type"
                                        outlined
                                        .height=${'28px'}
                                        class="input reward-criteria"
                                        placeholder="Distance from Me"
                                        @selected="${this.searchByDistance}"
                                        value="${this.search_distance}"
                                    >
                                        <mwc-list-item value="${1}">${1} ${kilometersOrMiles()}</mwc-list-item>
                                        <mwc-list-item value="${5}">${2} ${kilometersOrMiles()}</mwc-list-item>
                                        <mwc-list-item value="${10}">${10} ${kilometersOrMiles()}</mwc-list-item>
                                        <mwc-list-item value="${25}">${25} ${kilometersOrMiles()}</mwc-list-item>
                                        <mwc-list-item value="${50}">${50} ${kilometersOrMiles()}</mwc-list-item>
                                        <mwc-list-item value="${100}">${100} ${kilometersOrMiles()}</mwc-list-item>
                                        <mwc-list-item value="${0}">${this.search_distance === 0 ? 'Distance from Me': 'None'}</mwc-list-item>
                                    </proxi-selectfield>
                                ` : ''}
                                ${pointCache.hasFilterApplied ? html`
                                    <form-button
                                        .icon=${'clear'}
                                        variant="outline"
                                        size="sm"
                                        @click=${this.clearFilters}
                                    >Clear Filters</form-button>
                                ` : ''}
                            </div>
                            <div class="view_options">
                                  ${!pointCache.hasFilterApplied ? html`
                                    <span style="display: flex;
                                    align-items: center;
                                    gap: 5px;"> Sort:
                                    <proxi-selectfield
                                        id="submission_type"
                                        outlined
                                        .height=${'28px'}
                                        class="input reward-criteria"
                                        @selected="${this.handleChangeTab}"
                                        value="${this.selected_tab}"
                                    >
                                        ${this.options.map((option) => html`
                                            <mwc-list-item value="${option}">${option}</mwc-list-item>
                                        `)}
                                    </proxi-selectfield>
                                    </span>
                                ` : ''}
                                ${this.listView === PointCardView.Table ? html`
                                    <form-button
                                        .icon=${'view_list'}
                                        variant="outline"
                                        size="sm"
                                        .backgroundColor=${topicData.backgroundColor}
                                        .textColor=${topicData.textColor}
                                        @click=${() => {this.listView = PointCardView.Table; this.itemsPerPage = 10;}}
                                    ></form-button>
                                ` : html`
                                    <form-button
                                        .icon=${'view_list'}
                                        variant="outline"
                                        size="sm"
                                        @click=${() => {this.listView = PointCardView.Table; this.itemsPerPage = 10;}}
                                    ></form-button>
                                `}

                                <!-- <form-button
                                    .icon=${'view_agenda'}
                                    variant="outline"
                                    size="sm"
                                    @click=${() => {this.listView = PointCardView.List;}}
                                ></form-button> -->
                                ${this.listView === PointCardView.Cards ? html`
                                    <form-button
                                        .icon=${'view_module'}
                                        variant="outline"
                                        size="sm"
                                        .backgroundColor=${this.listView === PointCardView.Cards ? topicData.backgroundColor : ''}
                                        .textColor=${this.listView === PointCardView.Cards ? topicData.textColor : ''}
                                        @click=${() => {this.listView = PointCardView.Cards; this.itemsPerPage = 3 * (detectMobileViewport() ? 2 : 3);}}
                                    ></form-button>
                                ` : html`
                                    <form-button
                                        .icon=${'view_module'}
                                        variant="outline"
                                        size="sm"
                                        @click=${() => {this.listView = PointCardView.Cards; this.itemsPerPage = 3 * (detectMobileViewport() ? 2 : 3);}}
                                    ></form-button>
                                `}
                                <!-- <form-button
                                    .icon=${'map'}
                                    variant="outline"
                                    size="sm"
                                ></form-button> -->
                            </div>
                        </div>
                        ${this.showSummaryList ? html`
                            <div class="summary__list">
                                <div class="summary__list__header">
                                    <h2>All Places</h2>
                                </div>
                                <div class="summary__list__content">
                                    ${this.sorted_points.map(
                                        ([section_name, points]) => html`
                                            ${points.length === 0 ? '' : html`
                                                <p><b>${this.selected_tab === 'Chronological' ? this.formattedDate(section_name) : section_name}</b></p>
                                                ${points.map((point) => html`
                                                    <span style="cursor: pointer;">
                                                        ${point.name}
                                                </span>
                                            `)}
                                            `}
                                        `
                                    )}
                                </div>
                            </div>
                        ` : ''}
                    </div>
                <div class="${this.listView === PointCardView.Table ? 'points__spreadsheet_content' : 'points__middle_content'}" style="${inIframe() ? '' : 'padding:5px 20px;'}">
                    ${this.sorted_points.length === 0 ? html`
                        <div class="points__header">
                            <h2>No points found</h2>
                        </div>
                    ` :
                        this.listView === PointCardView.Table ? this.renderSpreadsheetView(pointCache.getSortedPoints()) :
                        html`
                            ${this.sorted_points.map(
                                ([section_name, points]) => html`
                                    ${points.length === 0 ? '' : html`
                                        <h1 style="font-size: 1.5rem; margin-top: 20px;">
                                            ${this.selected_tab === 'Chronological' ? section_name === 'No Date' ? 'No Date' : this.formattedDate(section_name) : section_name}
                                        </h1>
                                        ${this.selected_tab === 'Category' && topicData.categories.find((c) => c.name === section_name)?.description
                                            ? html`<p>${topicData.categories.find((c) => c.name === section_name)?.description}</p>`
                                            : ''}
                                        ${this.renderCards(points, section_name)}
                                        <hr>
                                    `}
                            `)}
                    `}
                        </div>
                    </div>
                </div>
            `;
    }

    renderCards(points: GetPointResponse[], group_name: string) {
        if (this.listView === PointCardView.Cards) {
            return this.renderGridCards(points, group_name);
        } else {
            return this.renderListCards(points, group_name);
        }
    }

    updateRenderedRows(points: GetPointResponse[]) {
        const rows = points.map((place) => this.renderSpreadsheetRow(place));
        this.renderedRows = rows;
    }

    renderSpreadsheetView(points: GetPointResponse[]) {
        const columns = this.calculateColumns();
        const gridTemplateColumns = `repeat(${columns}, 1fr)`;

        const endIndex = this.currentSpreadsheetPage * this.itemsPerPage;
        const paginatedPoints = points.slice(0, endIndex);

        // Update rendered rows
        this.updateRenderedRows(paginatedPoints);

        return html`
            <div class="spreadsheet-view-holder">
                <div class="grid-container" style="grid-template-columns: ${gridTemplateColumns}">
                    ${this.renderSpreadsheetHeader()}
                    ${this.renderedRows}
                </div>
                ${endIndex < points.length ? html`
                    <div class="hover" style="width:100%; height: 100px; text-align:center; padding-top:20px" @mouseenter=${this.handleHover} @touchstart=${this.handleHover}>
                        <b>Load more...</b>
                    </div>
                ` : ''}
            </div>
        `;
    }

    get hasDescription() {
        return topicData.topic?.add_point_customization?.description?.hide_on_point_card !== true && pointCache.allPoints.some((p) => p.description);
    }

    get hasSpecialOffer() {
        return topicData.topic?.add_point_customization?.special_offer?.hide_on_point_card !== true && pointCache.allPoints.some((p) => p.special_offer?.description);
    }

    isButton(field: CustomFieldCustomization) {
        return 'URL' in field.field_type && field.field_type.URL.show_as_button === true;
    }

    hideCustomField(field: CustomFieldCustomization) {
        return field.customizations.hide_on_point_card === true;
    }

    renderSpreadsheetHeader() {
        const hasDates = topicData.topic?.has_dates === true && pointCache.allPoints.some((p) => p.start);
        const hasTags = topicData.allUserTags.length > 0 && topicData.topic?.add_point_customization?.tags?.hide_on_point_card !== true;
        const hasName = topicData.topic?.add_point_customization?.title?.hide_on_point_card !== true;
        const extraFields = topicData.topic?.add_point_customization?.custom_fields?.filter((field) => !this.hideCustomField(field) && !this.isButton(field)).map((field) => field) ?? [];
        if (!detectMobileViewport()) {
            return html`
                <div class="grid-header">
                    <div class="name-category-cell">${hasName ? topicData.topic?.point_name ?? 'Place' : '---'}</div>
                    ${this.hasDescription ? html`<div class="description-cell">${topicData.topic?.add_point_customization?.description?.header ?? 'Description'}</div>` : ''}
                    ${hasDates ? html`<div>${topicData.topic?.has_time_only === true ? 'Time' : 'When'}</div>` : ''}
                    ${hasTags ? html`<div>${topicData.topic?.add_point_customization?.tags?.header ?? 'Tags'}</div>` : ''}
                    ${ extraFields.length >0 ? extraFields.map((field) => html`<div>${field.customizations.header}</div>`) : ''}
                    ${this.hasSpecialOffer ? html`<div>${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Special Offer'}</div>` : ''}
                </div>
            `;
        } else {
            return html `
            <div class="grid-header">
                    <div class="name-category-cell">${hasName ? topicData.topic?.point_name ?? 'Place' : '---'}</div>
                </div>
            `;
        }
    }

    renderSpreadsheetRow(place: GetPointResponse) {
        const hasDates = topicData.topic?.has_dates === true;
        const hasTags = topicData.allUserTags.length > 0 && topicData.topic?.add_point_customization?.tags?.hide_on_point_card !== true;
        const hasLocation = topicData.topic?.add_point_customization?.location?.hide_on_point_card !== true;
        const hasName = topicData.topic?.add_point_customization?.title?.hide_on_point_card !== true;
        const extraFields = topicData.topic?.add_point_customization?.custom_fields?.filter((field) => !this.hideCustomField(field) && !this.isButton(field)).map((field) => field) ?? [];

        const location = place.location.place_id === 'shape' ? 'Custom Shape' : place.location.place_id === 'custom' ? `${place.location.latLng.lat}, ${place.location.latLng.lng}` : place.location.search;
        const dateTimeString = hasDates && topicData.topic ? this.event_strings[place._id.$oid] : '---';
        const showMoreDesc = this.showMoreDescMap.get(place._id.$oid) ?? false;

        if (!detectMobileViewport()) {
            return html`
                <div class="grid-row ${place.url ? 'clickable-row' : ''}">
                    <div class="name-category-cell">
                        <b>${hasName ? html`<h3 class="${place.url ? 'clickable-row' : ''}">${place.name}</h3>` : '---'}</b>
                        ${html`${topicData.categories.find((c) => c._id?.$oid === place.topic_category_id?.$oid)?.name}`}
                        <br> ${hasLocation ? html`<br><i>${location}</i>` : ''}
                        <point-card-actions
                            style="padding: 0"
                            .point=${place}
                            .showDirections=${false}
                            .hideCheckin=${true}
                            .hideCreateMap=${true}
                        ></point-card-actions>
                    </div>
                    ${this.hasDescription ? html`<div class="description-cell" hover-text="${place.description}">
                        <div style="width: 100%; height: ${showMoreDesc ? 'auto' : '50px'}; overflow: hidden;">
                            ${unsafeHTML(parseDescriptionText(place.description ?? ''))}
                        </div>
                        ${!showMoreDesc && (place.description?.length ?? 0) > 150 ? html`<br><span style="cursor:pointer" @click=${() => {this.toggleShowMoreDesc(place._id.$oid);}}>Show More</span>` : ''}
                    </div>` : ''}
                    ${hasDates ? html`<div>${dateTimeString}</div>` : ''}
                    ${hasTags ? html`<div>${place.tag_info?.user_tags.map((tag) => tag.name).join(', ') ?? '---'}</div>` : ''}
                    ${extraFields.length >0 ? extraFields.map((field) => html`<div>${place.custom_information?.custom_field_entries?.find((entry) => entry.field_id.$oid === field._id?.$oid)?.field_value ?? '---'}</div>`) : ''}
                    ${this.hasSpecialOffer ? html`<div>${place.special_offer?.description ?? '---'}</div>` : ''}
                </div>
            `;
        } else {
            return html`
                <div class="grid-row ${place.url ? 'clickable-row' : ''}">
                    <div class="name-category-cell">
                        <b>${hasName ? html`<h3 class="${place.url ? 'clickable-row' : ''}">${place.name}</h3>` : '---'}</b>
                        ${html`${topicData.categories.find((c) => c._id?.$oid === place.topic_category_id?.$oid)?.name}`}
                        <br>
                        ${hasDates && isDefined(place.start) ? html`<br><i>${dateTimeString}</i><br>` : ''}
                        ${hasLocation ? html`<br><i>${location}</i>` : ''}
                        ${this.hasSpecialOffer ? html`<br>${place.special_offer?.description ?? '---'}<br>` : ''}
                        ${hasTags && ((place.tag_info?.user_tags.length ?? 0) > 0) ? html`<br>${place.tag_info?.user_tags.map((tag) => tag.name).join(', ') ?? '---'}<br>` : ''}
                        ${extraFields.length >0 ? extraFields.map((field) => html`<br>${place.custom_information?.custom_field_entries?.find((entry) => entry.field_id.$oid === field._id?.$oid)?.field_value ?? '---'}<br>`) : ''}
                        ${this.hasDescription ? html`<div class="description-cell" hover-text="${place.description}">
                            <div style="width: 100%; height: ${showMoreDesc ? 'auto' : 'fit-content'}; max-height: ${showMoreDesc ? 'none' : '50px'}; overflow: hidden;">
                                ${unsafeHTML(parseDescriptionText(place.description ?? ''))}
                            </div>
                            ${!showMoreDesc && this.isDescriptionLong(place.description) ? html`<br><span style="cursor:pointer" @click=${() => {this.toggleShowMoreDesc(place._id.$oid);}}>Show More</span>` : ''}
                        </div>` : ''}
                        <point-card-actions
                            style="padding: 0;"
                            .point=${place}
                            .showDirections=${false}
                            .hideCheckin=${true}
                            .hideCreateMap=${true}
                        ></point-card-actions>
                    </div>
                </div>
            `;
        }
    }

    private isDescriptionLong(description?: string): boolean {
        if (!description) return false;

        // More intelligent detection
        const plainText: string = description.replace(/<[^>]*>/g, ''); // Strip HTML
        const words: string[] = plainText.split(' ');
        const lines: string[] = plainText.split('\n');

        return (
            plainText.length > 300 ||  // Character count
            words.length > 40 ||       // Word count
            lines.length > 4           // Line breaks
        );
    }

    toggleShowMoreDesc(id: string) {
        const current = this.showMoreDescMap.get(id) ?? false;
        this.showMoreDescMap.set(id, !current);
        // if it is in card view, close all other cards
        if (this.listView === PointCardView.Cards) {
            for (const [key] of this.showMoreDescMap) {
                if (key !== id) {
                    this.showMoreDescMap.set(key, false);
                }
            }
        }
        this.requestUpdate();
    }

    async loadMorePoints() {
        this.loadingMore = true;
        this.currentSpreadsheetPage += 1;
        this.loadingMore = false;
    }

    renderPaginationControls(totalItems: number) {
        const totalPages = Math.ceil(totalItems / this.itemsPerPage);
        const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
        return html`
            <div class="pagination-controls">
                ${this.currentSpreadsheetPage > 1 ? html`
                    <form-button @click=${() => this.changePage(this.currentSpreadsheetPage - 1)} variant="outline">Previous</form-button>
                ` : ''}
                ${pages.map((page) => html`
                        <form-button .variant="${this.currentSpreadsheetPage === page ? '' : 'outline'}" @click="${() => this.changePage(page)}">${page}</form-button>
                    `)}
                ${this.currentSpreadsheetPage < totalPages ? html`
                    <form-button ?disabled=${this.currentSpreadsheetPage === totalPages} @click=${() => this.changePage(this.currentSpreadsheetPage + 1)} variant="outline">Next</form-button>
                ` : ''}
            </div>
        `;
    }

    changePage(page: number) {
        this.currentSpreadsheetPage = page;
    }

    renderGridCards(points: GetPointResponse[], group_name: string) {
        // get element from sorted points and get the last number which is the page number
        const currentPage = this.sorted_points.find(([name, points, page]) => name === group_name)?.[2] ?? 1;
        const noMorePages = points.length <= currentPage * this.itemsPerPage;
        const endIndex = (currentPage * this.itemsPerPage) - (noMorePages ? 0 : 1);
        const paginatedPoints = points.slice(0, endIndex);
        const columns = detectMobileViewport() ? 2 : 3;
        const width = detectMobileViewport() ? '49%' : '33%';
        return html`
            <div class="grid-view-holder">
                ${paginatedPoints.map((place, i) => {
                    return html`
                        ${this.showMoreDescMap.get(place._id.$oid) !== true ? html`
                            <grid-point-card .point=${place} @on-card-click="${async () => { this.toggleShowMoreDesc(place._id.$oid);
                            }}" id="point-${place._id.$oid}" .columns=${columns}>
                            </grid-point-card>
                        ` : html`
                            <div style="width: 100%; display: flex; box-shadow: 0px 0px 4px 0px var(--theme-border-color);">
                                ${this.renderSpreadsheetRow(place)}
                            </div>
                        `}
                    `;
                })}
                ${noMorePages ? '' : html`
                    <div class="load-more" @mouseenter=${() => this.goToNextPageByGroup(group_name)} @touchstart=${() => this.goToNextPageByGroup(group_name)}
                        style="width: min(${width} - 10px, 250px); height: 100px; margin: 5px;">
                        <mwc-icon style="--mdc-icon-size: 32px;">expand_more</mwc-icon>
                        <b>Load More</b>
                    </div>
                `}
            </div>
        `;
    }

    renderListCards(points: GetPointResponse[], group_name?: string) {
        return html`
            <div class="list-view-holder">
                ${points.map((place, i) => {
                    return html`
                    <list-point-card .point=${place} @on-card-click="${async () => {}}" id="point-${place._id.$oid}">
                    </list-point-card>`;
                    }
                )}
            </div>
        `;
    }

    calculateColumns() {
        let columns = 3;
        const isMobile = detectMobileViewport();
        if (!isMobile) {
            if (this.hasDescription) columns += 3;
            if (topicData.topic?.has_dates === true) columns++;
            if (topicData.allUserTags.length > 0 && topicData.topic?.add_point_customization?.tags?.hide_on_point_card !== true) columns++;
            if (this.hasSpecialOffer) columns++;
            for (const field of topicData.topic?.add_point_customization?.custom_fields ?? []) {
                if (!this.hideCustomField(field) && !this.isButton(field)) {
                    columns++;
                }
            }
        }
        return columns;
    }

    static get styles() {
        return css`

            :host {
                display: flex;
                flex-direction: column;
                --custom-color: green;
                --custom-text-color: #000;
            }

            .form-holder {
                display: flex;
                justify-content: center;
                padding: 3rem 20px;
            }

            .summary__page_wrapper {
                display: flex;
                flex-direction: column;
                align-items: center;
                min-height: 100vh;
                padding: 0.75rem;
                overflow-y: auto;
            }

            .summary__page_wrapper::-webkit-scrollbar {
                display: none;
            }

            .fixed_top {
                height: 25px;
                width: 100%;
                position: fixed;
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 100;
                top: 0;
            }

            .points__header {
                display: flex;
                flex-direction: column;
                align-items: center;
                margin-top: 5vh;
                padding: 0 20px;
            }

            .points__header img {
                display: block;
                margin: 0 auto;
            }

            .thead {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .share__buttons {
                display: flex;
                flex-direction: row;
                justify-content: center;
                width: 100%;
                margin-top: 5px;
            }

            h1 {
                font-size: 3rem;
                margin-top: 0;
                margin-bottom: 5px;
                text-align: center;
            }

            h2 {
                margin-top: 0;
                margin-bottom: 15px;
                text-align: center;
            }

            h3 {
                margin-top: 0;
                margin-bottom: 0;
            }

            .name-category-cell h3 .clickable-row {
                cursor: pointer;
            }

            .name-category-cell h3 .clickable-row:hover {
                color: var(--main-primary-color);
                text-decoration: underline;
            }

            vaadin-combo-box {
                height: 28px;
            }

            vaadin-combo-box::part(input-field) {
                background-color: white;
                border: 1px solid var(--main-primary-color);
                border-radius: 5px;
                height: 28px;
            }

            vaadin-combo-box-item::part(checkmark) {
                color: var(--main-primary-color);
            }

            th {
                padding: 10px;
                font-size: 1rem;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .points__spreadsheet_content {
                overflow-y: scroll;
                word-break: break-word;
            }

            tr {
                border-bottom: 1px solid var(--theme-border-color);
            }

            tr:hover {
                background-color: var(--theme-border-color);
            }

            td {
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .pagination-controls {
                display: flex;
                justify-content: center;
                gap: 8px;
                margin-top: 16px;
                position: -webkit-sticky; /* For Safari */
                position: sticky;
                bottom: 0;
                background-color: white;
                padding: 8px;
                z-index: 100;
                align-items: center;
            }

            .points__content_wrapper {
                display: flex;
                flex-direction: column;
                width: 100%;
                max-width: 1200px;
                justify-content: center;
                background-color: white;
                gap: 0.25rem;
                min-height: 100vh;
            }

            mwc-list-item::selected {
                color: var(--main-secondary-color);
            }

            table {
                width: 100%;
                border-collapse: collapse;
                background-color: white;
                border-spacing: 0;
                table-layout: fixed;
            }

            .filter_options {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: flex-start;
                gap: 10px;
            }

            .view_options {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: flex-end;
                gap: 5px;
                margin-bottom: 10px;
            }

            .description_section {
                font-size: 1rem;
                text-align: center;
            }

            hr {
                width: 70%;
                padding-left: 10%;
                padding-right: 10%;
            }

            .list-view-holder {
                width: 100%;
            }

            .summary__list {
                display: flex;
                flex-direction: column;
                padding: 1rem;
                background-color: var(--theme-border-color);
                max-width: min(calc(100% - 40px), 700px);
                margin: 0 auto;
            }

            tr {
                text-align: left;
                border-bottom: 1px solid var(--theme-border-color);
            }

            td {
                padding: 10px;
            }

            .description-span {
                min-width: 10vw;
                display: block;
                max-height: 100px;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .summary__list__header {
                margin-bottom: 10px;
            }

            .summary__list__content {
                display: flex;
                flex-flow: column wrap;
                gap: 2px 20px;
                max-height: 40vh;
                overflow-x: scroll;
                align-content: flex-start;
                align-items: flex-start;
                padding-bottom: 20px;
            }

            .filter_area {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                gap: 10px;
                margin: 10px 0px;
                padding-left: 10%;
                padding-right: 10%;
            }

            .grid-view-holder {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 10px;
                justify-content: center;
                width: 100%;
            }

            .filter_area_sticky {
                position: -webkit-sticky; /* For Safari */
                position: sticky;
                z-index: 101;
                background-color: white;
            }

            .points__view_map {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                --btn-label-color: white;
                border-radius: 10px 10px 0 0;
            }

            .points__middle_content {
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 25px;
                max-width: min(calc(100% - 40px), 1200px);
                min-width: min(500px, 100%);
                padding: 10px 20px;
                margin: 0 auto;
            }

            .points__spreadsheet_container {
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 25px;
                max-width: min(calc(100% - 20px), 1500px);
                padding: 10px;
            }

            .load-more {
                border: 2px dashed var(--theme-border-color);
                background-color: rgba(0, 0, 0, 0.02);
                word-wrap: break-word;
                border-radius: 8px;
                display: flex;
                flex-direction: column;
                height: 150px;
                cursor: pointer;
                font-weight: 400;
                justify-content: center;
                align-items: center;
                gap: 8px;
                transition: all 0.2s ease;
            }

            .load-more:hover {
                background-color: var(--theme-border-color);
                border-color: var(--main-primary-color);
                transform: translateY(-2px);
            }

            p {
                margin-top: 5px;
                margin-bottom: 0;
            }

            h2 {
                margin-bottom: 0px;
            }

            h4 {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .inline_words {
                display: inline-flex;
                flex-direction: row;
                align-items: flex-end;
                justify-content: flex-start;
                gap: 5px;
            }

            .inline_words p {
                margin-bottom: 5px;
            }

            .inline_words h2 {
                margin-top: 0;
            }

            .sparkles {
                height: 20px;
            }

            .hide_on_desktop {
                display: none;
            }

            .hidden {
                display: none;
            }

            .grid-container {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                width: 100%;
                gap: 0px;
            }

            .grid-header, .grid-row {
                display: contents;
            }

            .grid-header {
                background-color: var(--custom-color);
            }

            .grid-header > div, .grid-row > div {
                padding: 10px;
                border-bottom: 1px solid var(--theme-border-color);
            }

            .grid-header > div {
                font-weight: bold;
                background-color: var(--custom-color);
                color: var(--custom-text-color);
                display: flex;
                align-items: flex-start;
            }

            .grid-row > div {
                background-color: white;
                word-break: break-word;
            }

            .grid-row:hover > div {
                background-color: var(--theme-border-color);
            }

            .name-category-cell {
                grid-column: span 3;
                display: flex;
                flex-direction: column;
                gap: 4px;
                align-content: flex-start;
            }

            .description-cell {
                grid-column: span 3;
            }

            .description-span {
                min-width: 10vw;
                display: block;
                max-height: 100px;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .actions-cell {
                grid-column: span 2;
            }

            @media only screen and (max-width: 550px) {

                .points__middle_content {
                    flex-direction: column;
                    align-items: center;
                    min-width: unset;
\                }

                .filter_area {
                    flex-wrap: wrap;
                }

                .hide_on_mobile {
                    display: none;
                }

                .hide_on_desktop {
                    display: flex;
                }

                .name-category-cell {
                    grid-column: span 3;
                }

                .actions-cell {
                    grid-column: span 1;
                }

                .grid-row {
                    font-size: 0.65rem;
                }

                .filter_options {
                    flex-wrap: wrap;
                    justify-content: flex-start;
                }

            }
        `;
    }
}

